import React, { useMemo, useState } from 'react';

// Object-level "Print Report" picker. User click-orders inspections;
// the click order becomes the chapter order in the merged PDF.
//
//   inspections : array of { id, type, name, status, ... }
//   onCancel    : () => void
//   onConfirm   : async (ordered_inspection_ids: UUID[]) => void
//                  caller does the API call + opens the PDF

const TYPE_LABEL = {
  'VT-OSI': 'Visual Out of Service Inspection',
  'VT-ISI': 'Visual In Service Inspection',
  'VT':     'Visual Inspection',
  'UT-OSI': 'Ultrasonic Out of Service Inspection',
  'UT-ISI': 'Ultrasonic In Service Inspection',
  'UT':     'Ultrasonic Inspection',
  'DEFO':   'Deformation Measurement Analysis',
  'MT-OSI': 'Magnetic Out of Service Inspection',
  'MT-ISI': 'Magnetic In Service Inspection',
  'MT':     'Magnetic Inspection',
  'PT-OSI': 'Penetrant Out of Service Inspection',
  'PT-ISI': 'Penetrant In Service Inspection',
  'PT':     'Penetrant Inspection',
  'CW-OSI': 'Corner-weld Internal Dimension Inspection',
};

export default function PrintObjectReportModal({ inspections, onCancel, onConfirm }) {
  // order: array of inspection ids in click order
  const [order, setOrder] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  // Output format: 'pdf' (default — one merged PDF) or 'docx' (ZIP of
  // each chapter as its own .docx, no LibreOffice conversion).
  const [format, setFormat] = useState('pdf');

  const sortedInspections = useMemo(() => {
    return [...(inspections || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [inspections]);

  function toggle(id) {
    setError('');
    setOrder((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function handleConfirm() {
    if (order.length === 0) {
      setError('Select at least one inspection.');
      return;
    }
    setRunning(true);
    setError('');
    try {
      await onConfirm(order, format);
    } catch (err) {
      setError(err?.message || 'Failed to generate report.');
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={running ? undefined : onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{order.length > 1 ? 'Print Combined Report' : 'Print Report'}</h2>
          <button className="modal-close" onClick={onCancel} disabled={running}>✕</button>
        </div>

        <p className="confirm-text">
          Click one or more inspections in the order you want them to appear in the PDF.
        </p>

        {inspections.length === 0 ? (
          <div className="site-select-empty">
            <div className="site-select-empty-icon">📄</div>
            <p>No inspections on this object yet.</p>
          </div>
        ) : (
          <div className="site-select-list">
            {sortedInspections.map((insp) => {
              const orderIdx = order.indexOf(insp.id);
              const isSelected = orderIdx >= 0;
              const label = TYPE_LABEL[insp.type] || insp.type || '(unknown type)';
              return (
                <div
                  key={insp.id}
                  className={`site-select-item print-report-item${isSelected ? ' insp-wizard-op-selected' : ''}`}
                  onClick={() => !running && toggle(insp.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div className="site-select-item-name">{label}</div>
                  </div>
                  {isSelected ? (
                    <span
                      className="insp-wizard-op-selected-icon"
                      title={`Selection order: ${orderIdx + 1}`}
                      style={{ minWidth: 22, textAlign: 'center', fontWeight: 700 }}
                    >
                      {orderIdx + 1}
                    </span>
                  ) : (
                    <span className="site-select-item-arrow">›</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {error && <div className="alert alert-danger" style={{ marginTop: 'var(--space-sm)' }}>{error}</div>}

        {/* Output-format toggle. PDF (default) = one merged PDF, opens
            in a new tab. DOCX = a ZIP archive with every chapter as its
            own editable .docx — skips LibreOffice. */}
        <div className="print-format-row">
          <span className="print-format-label">Output</span>
          <div className="print-format-toggle" role="radiogroup" aria-label="Output format">
            <button
              type="button"
              role="radio"
              aria-checked={format === 'pdf'}
              className={`print-format-btn${format === 'pdf' ? ' active' : ''}`}
              onClick={() => !running && setFormat('pdf')}
              disabled={running}
            >
              PDF
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={format === 'docx'}
              className={`print-format-btn${format === 'docx' ? ' active' : ''}`}
              onClick={() => !running && setFormat('docx')}
              disabled={running}
            >
              DOCX (.zip)
            </button>
          </div>
          <span className="print-format-hint">
            {format === 'pdf'
              ? 'Merged PDF, opens in a new tab.'
              : 'One .docx per chapter, packed into a single ZIP.'}
          </span>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={running}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={running || order.length === 0}
          >
            {running
              ? 'Building…'
              : `Generate ${format === 'docx' ? 'DOCX' : 'PDF'} (${order.length} selected)`}
          </button>
        </div>
      </div>
    </div>
  );
}
